import type { FunctionComponent, RefObject, TargetedEvent, TargetedKeyboardEvent } from 'preact';
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';

import { SearchBoxDialogName } from 'Components/names';
import type {
  TemplateContextMap,
  TemplateFunction,
  TemplateFunctionInvoker,
  TemplateResult,
} from 'Components/types';
import { clearAutocompleteQuery, sendAutocompleteRequest } from 'Core/actions/autocomplete';
import { dialogClosed } from 'Core/actions/dialog';
import { replaceRequest } from 'Core/actions/request';
import { useDropdown } from 'Core/hooks/index';
import {
  autocompleteDefaultSuggestionsSelector,
  autocompleteRequestQuerySelector,
  autocompleteResponseItemsSelector,
} from 'Core/selectors/autocomplete';
import { autocompletePreselectionSelector } from 'Core/selectors/preselection';
import { useDispatch, useSelector } from 'Core/store';
import type { FacetValueBase } from 'Models/index';
import requestConfig from 'Models/uiConfig/requestConfig';
import { cloneSafe, preventFormSubmission } from 'Utils/components';
import { simpleHandler } from 'Utils/roleHandler';
import safeTemplateCall from 'Utils/safeTemplateCall';

import SearchDropdown, { Params as SearchDropdownParams } from './searchDropdown';
import SearchInput, { Params as SearchInputParams } from './searchInput';

const rootClass = 'cm_search-box-root';

type SearchBoxReplaceRequestPayload =
  | { query: string; checkAutocompleteRedirect: true }
  | { selection: FacetValueBase[]; query?: string };

export type Params = {
  searchInput: TemplateFunctionInvoker<SearchInputParams>;
  dropdown: TemplateFunctionInvoker<SearchDropdownParams>;
  preventFormSubmission: (e: TargetedEvent<HTMLFormElement, Event>) => void;
  inputNotEmpty: boolean;
  clearInput: () => void;
} & TemplateContextMap;

export type Props = {
  template: TemplateFunction<Params>;
  name: string;
  disableDropdown?: true;
  redirectUrl?: string;
  disableAutoRequest?: boolean;
  onSubmit?: () => void;
  onInputFocus?: () => void;
  onDropdownItemsReceived?: () => void;
  extraParams?: TemplateContextMap;
  useSuggestionsFilter?: true;
  enableAutoFocus?: boolean;
  inputRef?: RefObject<HTMLInputElement>;
};

const SearchBox: FunctionComponent<Props> = ({
  template,
  name,
  disableDropdown,
  redirectUrl,
  disableAutoRequest,
  onSubmit,
  onInputFocus,
  onDropdownItemsReceived,
  extraParams,
  useSuggestionsFilter,
  enableAutoFocus,
  inputRef: providedInputRef,
}) => {
  const dispatch = useDispatch();
  const rootRef = useRef<HTMLElement>(null);
  const localInputRef = useRef<HTMLInputElement>(null);
  const inputRef = providedInputRef ?? localInputRef;

  const autocompleteResponse = useSelector(autocompleteResponseItemsSelector);
  const autocompleteQuery = useSelector(autocompleteRequestQuerySelector);
  const defaultSuggestion = useSelector(autocompleteDefaultSuggestionsSelector);
  const preselection = useSelector(autocompletePreselectionSelector);

  const [activeQuery, setActiveQuery] = useState(autocompleteQuery);
  const [previouslySubmittedQuery, setPreviouslySubmittedQuery] = useState('');

  const showDefaultSuggestions = !(activeQuery && activeQuery.length >= 3);
  const autocompleteItems = showDefaultSuggestions ? defaultSuggestion : autocompleteResponse;

  const { hideDropdown, showDropdown, keyboardHandler, resetNav } = useDropdown(
    disableDropdown || name === SearchBoxDialogName ? { current: null } : rootRef,
    `.${rootClass}`,
    { useClick: true },
  );

  const sendRequest = useCallback(
    (request: SearchBoxReplaceRequestPayload) => {
      dispatch(
        replaceRequest(
          {
            ...request,
            goToSearchPage: true,
            ignoreLocalPreselection: true,
            pathname: redirectUrl,
          },
          ['FilterByVehicle'],
        ),
      );
      resetNav();
      hideDropdown();
    },
    [dispatch, hideDropdown, redirectUrl, resetNav],
  );

  const submit = useCallback(
    (requestQuery: string = autocompleteQuery) => {
      onSubmit?.();
      setPreviouslySubmittedQuery(requestQuery);
      sendRequest({ checkAutocompleteRedirect: true, query: requestQuery });
    },
    [autocompleteQuery, onSubmit, sendRequest],
  );

  const clearInput = () => {
    dispatch(clearAutocompleteQuery());
    setActiveQuery('');
  };

  const onClick = simpleHandler({ addQuery: () => submit(autocompleteQuery) });
  const onKeyDown = (e: TargetedKeyboardEvent<HTMLElement>) => {
    keyboardHandler(e);
    if (e.key === 'Enter') {
      submit();
    }
  };

  const onFocus = () => {
    onInputFocus?.();

    if (!autocompleteQuery && !disableAutoRequest) {
      if (!defaultSuggestion) {
        dispatch(sendAutocompleteRequest({ query: activeQuery, selection: preselection }));
      }

      showDropdown();
    }
  };

  const searchInput = (templ: TemplateFunction<SearchInputParams>) => {
    const props = {
      template: templ,
      name,
      onChange: (query: string) => {
        showDropdown();
        setActiveQuery(query);
        setPreviouslySubmittedQuery('');
      },
      onFocus,
      rootRef,
      inputRef,
      enableAutoFocus,
    };
    return (<SearchInput {...props} key="input" />) as TemplateResult;
  };

  const setQuery = useCallback(
    (query: string) => {
      setActiveQuery(query);

      if (!useSuggestionsFilter) {
        sendRequest({ checkAutocompleteRedirect: true, query });

        if (requestConfig.hasSearchResults) {
          dispatch(dialogClosed(SearchBoxDialogName));
        }
      }
    },
    [dispatch, sendRequest, useSuggestionsFilter],
  );

  useEffect(
    function () {
      const root = rootRef.current;
      if (root) {
        const onSetQuery = (event: Event) => {
          if (!(event instanceof CustomEvent) || typeof event.detail !== 'string') {
            return;
          }
          setQuery(event.detail);
        };

        root.addEventListener('cm_set_query', onSetQuery, false);
        return () => root.removeEventListener('cm_set_query', onSetQuery, false);
      }
    },
    [setQuery, rootRef],
  );

  const dropdown = (templ: TemplateFunction<SearchDropdownParams>) => {
    if (disableDropdown) {
      return null;
    }

    const props = {
      template: templ,
      hideDropdown,
      items: autocompleteItems || [],
      query: activeQuery,
      previouslySubmittedQuery,
      showDefaultSuggestions,
      useSuggestionsFilter,
      setQuery,
      setSelection: (selection: FacetValueBase[], query?: string) => {
        query ? sendRequest({ selection, query }) : sendRequest({ selection });
      },
      submit,
      onDropdownItemsReceived,
    };
    return (<SearchDropdown {...props} key="dropdown" />) as TemplateResult;
  };

  const component = safeTemplateCall(
    template,
    {
      name,
      query: activeQuery,
      searchInput,
      setQuery,
      dropdown,
      preventFormSubmission,
      inputNotEmpty: !!autocompleteQuery,
      clearInput: () => {
        clearInput();
        inputRef.current?.focus();
      },
      ...extraParams,
    },
    'SearchBox',
  );

  return cloneSafe(component, rootRef, { onClick, onKeyDown, appendedClasses: rootClass });
};

export default SearchBox;
